
import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Container, Col, Row, Input, Button } from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { toast } from 'react-toastify';
import '../../aws-config';
import CustomPagination from '../../Components/Common/CustomPagination';
import CreateJobModal from './CreateJobModal';
import { useNavigate } from 'react-router-dom';
import { VideoProcessorAPI } from '../../helpers/firebase_helper';
const styles = {
    statusLabel: {
        display: 'inline-block',
        padding: '0.375rem 0.75rem',
        fontSize: '0.875rem',
        fontWeight: '500',
        borderRadius: '0.5rem',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        color: '#ffffff',
        border: 'none',
    },
    statusOpen: {
        backgroundColor: '#3577f1', // Blue
    },
    statusQualified: {
        backgroundColor: '#13c56b', // Green
    },
    statusDisqualified: {
        backgroundColor: '#865ce2', // Purple
    },
    statusCompleted: {
        backgroundColor: '#13c56b', // Green (same as Qualified)
    },
    statusInProgress: {
        backgroundColor: '#3577f1', // Blue (same as Open)
    },
    statusError: {
        backgroundColor: '#ed5e5e', // Red
    },
    statusPending: {
        backgroundColor: '#3577f1', // Blue (same as Open)
    },
    statusDefault: {
        backgroundColor: '#3577f1', // Blue (default to Open)
    },
};

const getStatusStyle = (status) => {
    const normalized = (status || '').toString().toLowerCase().trim();

    // Map to screenshot statuses
    if (['qualified', 'complete', 'completed', 'success', 'succeeded'].includes(normalized)) {
        return { ...styles.statusLabel, ...styles.statusQualified };
    }

    if (['disqualified', 'rejected', 'declined'].includes(normalized)) {
        return { ...styles.statusLabel, ...styles.statusDisqualified };
    }

    if (['open', 'processing', 'in_progress', 'running', 'progressing', 'queued', 'pending', 'scheduled', 'waiting', 'started', 'submitted'].includes(normalized)) {
        return { ...styles.statusLabel, ...styles.statusOpen };
    }

    if (['error', 'failed', 'failure'].includes(normalized)) {
        return { ...styles.statusLabel, ...styles.statusError };
    }

    // Default to Open (blue)
    return { ...styles.statusLabel, ...styles.statusOpen };
};

const convertUTCtoIST = (utcTimestamp) => {
    if (!utcTimestamp) return '';
    
    try {
        const date = new Date(utcTimestamp);
        const istString = date.toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
        
        const parts = istString.split(', ');
        const datePart = parts[0].split('/');
        const timePart = parts[1];
        const formattedDate = `${datePart[2]}-${datePart[1]}-${datePart[0]}`;
        return `${formattedDate} ${timePart}`;
    } catch (error) {
        console.error('Error converting timestamp:', error);
        return utcTimestamp; 
    }
};

const VideoProcessor = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [modal, setModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [jobsPerPage] = useState(10);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const toggleModal = () => {
        setModal(!modal);
    };

    const fetchJobs = async () => {
        try {
            setLoading(true);
            const response = await VideoProcessorAPI.fetchJobs();
            // console.log(response);
            const jobsData = response?.result?.jobs || [];
            setJobs(jobsData);
            toast.success('Jobs loaded successfully');
        } catch (error) {
            console.error('Error fetching jobs:', error);
            toast.error(error?.message || 'Failed to fetch jobs');
            setJobs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, []);

    const handleViewClick = (job) => {
        if (!job || !job.job_uuid) {
            toast.error('Error: Invalid job selected for View.');
            return;
        }

        navigate(`/video-processor/video-detail/${job.job_uuid}`);
    };

    const filteredVideos = jobs.filter(job => {
        return job.filename?.toLowerCase().includes(searchTerm.toLowerCase());
    });
    const sortedJobs = filteredVideos.slice().sort((a, b) => {
        const dateA = new Date(a.created_timestamp || a.created_at || 0);
        const dateB = new Date(b.created_timestamp || b.created_at || 0);
        return dateB - dateA;
    });

    const indexOfLastJob = currentPage * jobsPerPage;
    const indexOfFirstJob = indexOfLastJob - jobsPerPage;
    const currentJobs = sortedJobs.slice(indexOfFirstJob, indexOfLastJob);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    document.title = "Media Pulse | Video Processor";
    return (
        <div className='page-content'>
            <Container fluid>
                <BreadCrumb title="Video Processor" pageTitle="" />
                <Col>
                    <Card>
                        <CardHeader className="align-items-center">
                            <Row className="mb-3">
                                <h4 className="card-title mb-0 flex-grow-1">List Jobs</h4>
                            </Row>
                            <div className="mb-3 row">
                                <div className="border border-dashed border-end-0 border-start-0 card-body">
                                    <Row>
                                        <Col md={6}>
                                            <div className="search-box ms-2">
                                                <Input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search for job by name..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                                <i className="ri-search-line search-icon"></i>
                                            </div>
                                        </Col>
                                        <Col md={6} className="text-end">
                                            <div className="d-flex justify-content-end gap-2">
                                                <Button 
                                                    color="primary" 
                                                    onClick={fetchJobs}
                                                    disabled={loading}
                                                >
                                                    <i className="ri-refresh-line align-bottom me-1"></i> Refresh
                                                </Button>
                                                <button className="btn btn-success" onClick={toggleModal}>
                                                    <i className="ri-add-line align-bottom me-1"></i> Create Job
                                                </button>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </CardHeader>
                        <CardBody>

                            <div className="table-responsive table-card">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading jobs...</p>
                                    </div>
                                ) : (
                                    <>
                                        <table className="table table-borderless table-centered align-middle table-nowrap mb-0">
                                            <thead className="text-muted table-light">
                                                <tr>
                                                    <th scope="col">Job ID</th>
                                                    <th scope="col">Updated Timestamp</th>
                                                    <th scope="col">Job Name</th>
                                                    <th scope="col">Execution ID</th>
                                                    <th scope="col">Status</th>
                                                    <th scope="col">Details</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentJobs.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-4">
                                                            No jobs found
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    currentJobs.map((job, key) => {
                                            const statusStyle = getStatusStyle(job.status);
                                            return (
                                                <tr key={key}>
                                                    <td>{job.job_uuid}</td>
                                                    <td>{convertUTCtoIST(job.updated_at)}</td>
                                                    <td>{job.filename}</td>
                                                    <td>{job.execution_id}</td>
                                                    <td><span style={statusStyle}>{job.status}</span></td>
                                                    <td>
                                                        <Button style={{ backgroundColor: '#50c3e6', borderColor: '#50c3e6' }} onClick={() => handleViewClick(job)}>
                                                            View details
                                                        </Button>
                                                    </td>
                                                </tr>
                                                    );
                                                })
                                                )}
                                            </tbody>
                                        </table>
                                    </>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <div className="row g-0 text-center text-sm-start align-items-center mb-3">
                    <div className="col-sm-6">
                        <p className="mb-sm-0">
                            Showing {indexOfFirstJob + 1} to {Math.min(indexOfLastJob, filteredVideos.length)} of {filteredVideos.length} entries
                        </p>
                    </div>

                    <div className="col-sm-6">
                        <ul className="pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                            <CustomPagination
                                totalItems={filteredVideos.length}
                                itemsPerPage={jobsPerPage}
                                currentPage={currentPage}
                                onPageChange={paginate}
                            />
                        </ul>
                    </div>
                </div>

                <CreateJobModal
                    isOpen={modal}
                    toggle={toggleModal}
                />
                
            </Container>

        </div>
    );
};

export default VideoProcessor;

